import { useEffect, useState } from "react";
import styleSheet from "../style.module.scss";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Shared Components
import SyPopup from "../../../../../shared/components/sy-model";
import AppMenu from "../../../popups/app-menu-popup";
import SyIcon from "../../../../../shared/components/sy-icon";
import SyInput from "../../../../../shared/forms/sy-input-form";
import AuthLayoutPopup from "../../../../auth-module/popup/auth-layout-popup";
import OrbConfirmation from "../../../../../shared/components/orb-confirmation";

// Store & Services
import { getAuthDetails } from "../../../../../feature/auth-slice";
import {
  getOrbitDetails,
  setAdminStuff,
  setHoverPlanet,
  setMaps,
  setOrbitSearch,
  setSelectOrbit,
} from "../../../../../feature/orbit-slice";
import orbitService from "../../../services/orbit-service";

// Utils
import { getCookie } from "../../../../../utils/cookie";
import { useGetOrbitDetails } from "../../../../../utils/orbitdetails";
import { useSearchParams } from "react-router-dom";
import { orbitParamValue } from "../../../../../utils/helper";
import AppLanguage from "../app-language";
import LoadingScreen from "../../loading-screen";
import {
  getLoadingDetails,
  setloading,
} from "../../../../../feature/loading-slice";
import AddOrbitPopup from "../../../popups/add-orbit-popup";
import {
  getNotificationDetails,
  setNotification,
} from "../../../../../feature/notification-slice";
import { useNotifyApi } from "../../../../../utils/notifyHook";
import FilesCommentsManage from "../../../components/files-comments-manage";

type Props = {};

const AppHeader = (props: any) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { adminView, companyId, userId, encryptData, inviteEncryptData } =
    useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  // Store data
  const authSlice = useSelector(getAuthDetails);
  const orbitSlice = useSelector(getOrbitDetails);
  const loadingSlice = useSelector(getLoadingDetails);
  const notificationSlice = useSelector(getNotificationDetails);

  // Cookies
  const authToken = getCookie("auth_token");
  const showLoadingScreen = getCookie("loading");

  // Use State
  const [authPopupView, setAuthPopupView] = useState("login");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [openAuthPopup, setOpenAuthPopup] = useState(false);
  const [openOrbitPopup, setOpenOrbitPopup] = useState(false);
  const [openConfirmPopup, setConfirmPopup] = useState(false);
  const [openAppMenu, setOpenAppMenu] = useState(false);
   const [openCommentTab, setOpenCommentTab] = useState(false);

  // API hooks
  const { getDetail, isPending } = useGetOrbitDetails(true);

  const { mutate: acceptInvite } = useMutation({
    mutationFn: orbitService.acceptInvite,
    onSuccess: () => navigate("/"),
  });

  // Handlers
  const back = () => {
    dispatch(setSelectOrbit(null));
    dispatch(setMaps(0));
    dispatch(setAdminStuff(null));
    dispatch(setHoverPlanet(null));
    navigate("/");
  };
  const onClickNotification = () => {
    // onClickNotic();
        setOpenCommentTab(false)
    dispatch(setNotification(!notificationSlice.notification));
  };
  const home = () => {
    dispatch(setloading(true));
    const timeout = setTimeout(() => {
      dispatch(setSelectOrbit(authSlice.orbit_details[0].orb_id));
      navigate({
        pathname: "/orbit",
        search: `?fr=home`,
      });
    }, 100);
    return () => clearTimeout(timeout);
  };

  const addPlanet = () => {
    const params = orbitParamValue(searchParams);
    navigate({
      pathname: `/add/planet/${orbitSlice.selectOrbit}`,
      search: `?fr=${params.fr}`,
    });
  };

  const addOrbit = () => {
    setOpenOrbitPopup(true);
  };

  const onClickAdminTab = () => {
    const params = orbitParamValue(searchParams);
    setOpenCommentTab(false)
    if (adminView) {
      dispatch(setAdminStuff(!orbitSlice.adminStuff));
      navigate({
        pathname: "/orbit",
        search: `?fr=${params.fr}`,
      });
    } else {
      navigate({
        pathname: `/admin/${orbitSlice.selectOrbit}/planets`,
        search: `?fr=${params.fr}`,
      });
    }
  };

  const onClickLogin = () => {
    if (authToken && !authSlice.isVolunteer) {
      setOpenAppMenu(true);
    } else {
      setOpenAuthPopup(true);
    }
  };

  // Effects
  // useEffect(() => {
  //   if (userId) {
  //     setConfirmPopup(true);
  //   } else if (companyId) {
  //     setOpenAuthPopup(true);
  //   }
  // }, [companyId || showLoadingScreen || userId]);

  useEffect(() => {
    if (encryptData) {
      setOpenAuthPopup(true);
    } else if (inviteEncryptData) {
      setConfirmPopup(true);
    }
  }, [encryptData || inviteEncryptData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (!search || (search && debouncedSearch)) {
      dispatch(setOrbitSearch(search));
    }
  }, [debouncedSearch]);

  // useEffect(() => {
  //   console.log(authSlice,'authSlice')

  // }, [authSlice.orbitDetails]);

  // --- Extracted PopupComponents ---
  const onClickComment=()=>{
      setOpenCommentTab(!openCommentTab)
    }

  const popupComponents = () => {
    // Handlers
    const onCloseAuthPop = (type: any = null) => {
      setOpenAuthPopup(false);
      if (type === "save") {
        getDetail();
        // if (!isPending) {
        //   dispatch(setSelectOrbit(authSlice.orbit_details.orb_id));
        // }
      }
    };

    const onCloseConfirmPop = () => {
      setConfirmPopup(false);
    };

    const onCloseOrbitPop = (save: any) => {
      if (save) {
        getDetail();
        navigate({
          pathname: "/orbit",
          search: `?fr=home`,
        });
      }
      setOpenOrbitPopup(false);
    };

    const onConfirmClick = (val: any) => {
      setConfirmPopup(false);
      if (val) {
        const payload: any = {
          encrypt: inviteEncryptData,
        };
        acceptInvite(payload);
      } else {
        navigate("/");
      }
    };

    const onCloseMenuPopup = () => {
      setOpenAppMenu(false);
    };

    const onClickProfile = (type: any) => {
      const params = orbitParamValue(searchParams);

      setOpenAppMenu(false);

      const navigateOptions: any = {
        pathname: `/profile/${authSlice.user_details.usr_id}`,
      };

      if (params.fr) {
        navigateOptions.search = `?fr=${params.fr}`;
      }

      navigate(navigateOptions);
    };

    
    return (
      <>
        <SyPopup open={openOrbitPopup} onClose={onCloseAuthPop} maxWidth={"sm"}>
          <AddOrbitPopup onClose={onCloseOrbitPop} />
        </SyPopup>
        <SyPopup open={openAuthPopup} onClose={onCloseAuthPop} maxWidth={"sm"}>
          <AuthLayoutPopup onClose={onCloseAuthPop} view={authPopupView} />
        </SyPopup>

        <SyPopup
          open={openConfirmPopup}
          onClose={onCloseConfirmPop}
          maxWidth={"xs"}
        >
          <OrbConfirmation
            message={"Are you sure you want to join the orbit? "}
            onConfirmClick={onConfirmClick}
          />
        </SyPopup>

        <SyPopup
          open={openAppMenu}
          onClose={onCloseMenuPopup}
          position="top-right"
          backDrop={false}
          maxWidth="260px"
        >
          <AppMenu
            onClickClose={onCloseMenuPopup}
            onClickProfile={onClickProfile}
          />
        </SyPopup>
      </>
    );
  };

  // Render
  return (
    <>
      <div
        className={`${styleSheet.header} slide_in_top d-flex w-100 flex-row align-items-center justify-content-between position-fixed px-3 py-1 z-3`}
      >
        <div className="col-lg-4 ">
          {authSlice.company_details?.cmp_name ? (
            <p className={`m-0 text-nowrap text-truncate text-white`}>
              {authSlice.company_details?.cmp_name}
            </p>
          ) : null}
        </div>
        <div className="col-lg-4 col-xm-12">
          <div className="d-flex justify-content-center ">
            {!orbitSlice.selectOrbit && authSlice.orbit_details.length ? (
              <SyIcon
                className={"text-white p-2 pointer"}
                name={"app_badging"}
                iconType="symbols"
                iconStyle="outlined"
                onClick={home}
              />
            ) : null}

            {!orbitSlice.selectOrbit && !adminView && (
              <SyInput
                type={"text"}
                placeholder={t("search_orbit")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}

            {(orbitSlice.orbitAccess === 1 || orbitSlice.orbitAccess === 2) &&
              !orbitSlice.adminStuff &&
              orbitSlice.selectOrbit && (
                <div className={"py-1 col-4 d-flex justify-content-center "}>
                  <div
                    className="bg-white rounded d-flex align-items-center py-1 px-2 pointer"
                    onClick={addPlanet}
                  >
                    <SyIcon
                      className="pe-1"
                      name="add"
                      iconStyle="outlined"
                      iconType="symbols"
                    />
                    <p className="m-0 mohave text-nowrap d-none d-md-block">
                      {t("new_planet")}
                    </p>
                  </div>
                </div>
              )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="row">
            <div className="col ms-3">
              {authToken &&
                !orbitSlice.selectOrbit &&
                authSlice.user_details.usr_type === 1 && (
                  <div
                    className="bg-white rounded d-flex align-items-center py-1 mt-1 px-2 pointer"
                    onClick={addOrbit}
                  >
                    <SyIcon
                      className="pe-1"
                      name="add"
                      iconStyle="outlined"
                      iconType="symbols"
                    />
                    <p className="m-0 mohave text-nowrap d-none d-md-block">
                      {t("new_orbit")}
                    </p>
                  </div>
                )}
            </div>
            <div className="col">
              {(orbitSlice.orbitAccess === 1 || orbitSlice.orbitAccess === 2) &&
              !orbitSlice.adminStuff &&
              orbitSlice.selectOrbit && (
                // <span className="px-2 py-1 mt-1 bg-white rounded d-flex  align-items-center ">
              <SyIcon
                className="  pointer mt-2 text-white  "
                name="add_comment "
                iconStyle="outlined"
                iconType="symbols"
                onClick={onClickComment}
              />
              // </span>
              // <span className={`${styleSheet.cloud_button}`}>Click Me</span>
              )}
            </div>
            <div className="col">
              {orbitSlice.selectOrbit &&
                orbitSlice.orbitAccess === 1 &&
                !orbitSlice.adminStuff && (
                  <div
                    className="d-flex text-white pointer  align-items-center mt-2 pointer"
                    onClick={onClickAdminTab}
                  >
                    <>
                      <SyIcon
                        className="pe-1"
                        name={`shield_person`}
                        iconStyle="outlined"
                        iconType="symbols"
                      />

                      <p className="m-0 text-nowrap d-none d-md-block mohave">
                        {t("admin_stuff")}
                      </p>
                    </>
                  </div>
                )}
              {orbitSlice.adminStuff && (
                <div
                  className="d-flex text-white pointer  align-items-center mt-2 pointer"
                  onClick={onClickAdminTab}
                >
                  <>
                    <SyIcon
                      className="pe-1"
                      name={"chevron_left"}
                      iconStyle="outlined"
                      iconType="symbols"
                    />

                    <p className="m-0 text-nowrap d-none d-md-block mohave">
                      {t("back_to_orbit")}
                    </p>
                  </>
                </div>
              )}
            </div>

            <div className="col d-flex justify-content-end">
              {authSlice.authToken ? (
                <div
                  className="mt-2  position-relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="position-absolute  translate-middle ms-4 px-1 font-10 rounded text-white bg-danger">
                    {notificationSlice.notificationCount > 9
                      ? "+9"
                      : notificationSlice.notificationCount > 0
                      ? notificationSlice.notificationCount
                      : ""}
                  </span>
                  <SyIcon
                    className="pe-1 text-white pointer"
                    name={"notifications"}
                    iconStyle="outlined"
                    iconType="symbols"
                    onClick={onClickNotification}
                  />
                </div>
              ) : null}
            </div>

            <div className="col">
              <div className="row pe-2">
                <div className="col ">
                  <div className="d-flex justify-content-end align-items-center py-2">
                    <p
                      className={`m-0 text-nowrap text-truncate  mohave ${
                        openAppMenu ? "text-disabled" : "text-white"
                      }`}
                      onClick={onClickLogin}
                      role="button"
                    >
                      {authSlice.user_details?.usr_name &&
                      !authSlice.isVolunteer ? (
                        <span className="d-none d-sm-block">
                          {" "}
                          {authSlice.user_details.usr_name}
                        </span>
                      ) : (
                        t("log_in")
                      )}
                    </p>
                    {authSlice.user_details?.usr_name ? (
                      <SyIcon
                        name={"arrow_drop_down"}
                        className={` ${
                          openAppMenu
                            ? `${styleSheet.rotate} text-disabled`
                            : "text-white"
                        } `}
                        onClick={onClickLogin}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {openCommentTab &&(orbitSlice.orbitAccess === 1 || orbitSlice.orbitAccess === 2) &&
              !orbitSlice.adminStuff &&
              orbitSlice.selectOrbit  &&
      <div className={` position-absolute end-0 z-3 mt-5 me-3`}>
        <FilesCommentsManage comment={true} />
      </div>
      }
      {loadingSlice.loading && <LoadingScreen mode={"orbitLoading"} />}
      {popupComponents()}
    </>
  );
};

export default AppHeader;
